/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { ListItem, ListItemButton } from '@mui/material'
import { Text, Icon } from '@components/atoms'
import { DrawerMainMenuList } from '@components/systems/drawerMenu/DrawerMainMenuList'
import { DrawerMainMenuItem } from '@components/systems/drawerMenu/DrawerMainMenuItem'

import { useAppDispatch, useAppSelector, mainActions, MenuType } from '@redux/index'
import { useLocation } from 'react-router-dom'
import { grey } from '@mui/material/colors'

interface PropsType {
	menu: MenuType
}

export const DrawerGroupList = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const location = useLocation()

	// redux state
	const openedMenuIdArr: string[] = useAppSelector(state => state.main.openedMenuIdArr)
	const isOpenDrawer: boolean = useAppSelector(state => state.config.isOpenDrawer)

	// variables
	const isGroup: boolean =
		props.menu.children !== undefined && props.menu.children.length > 0
	const iconName: string =
		props.menu.icon !== undefined ? props.menu.icon : 'circle-medium'
	const samePathWithChildren: MenuType[] = React.useMemo(() => {
		if (props.menu.children !== undefined) {
			const res = props.menu.children.filter(aMainMenu => {
				const isMainMenuSelected: boolean =
					aMainMenu.to !== undefined && location.pathname === aMainMenu.to

				const selectedSubmenu: MenuType[] =
					aMainMenu.children !== undefined
						? aMainMenu.children.filter(
								aSubmenu =>
									aSubmenu.to !== undefined && location.pathname === aSubmenu.to,
						  )
						: []

				return isMainMenuSelected || selectedSubmenu.length > 0
			})

			return res
		} else {
			return []
		}
	}, [props.menu.children])

	const isSubMenuSelected: boolean =
		props.menu.children !== undefined && samePathWithChildren.length > 0
	const textColor: string =
		location.pathname === props.menu.to || isSubMenuSelected ? grey[800] : grey[400]
	const isOpened: boolean = openedMenuIdArr.includes(props.menu.id)

	// state
	const [isOpenMainMenu, setisOpenMainMenu] = React.useState<boolean>(isOpened)
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

	React.useEffect(() => {
		if (!isOpenDrawer) {
			setisOpenMainMenu(false)
		}
	}, [isOpenDrawer])

	const onOpenSubmenuClick = (event: any) => {
		const newOpenedMenuIdArr: string[] = isOpenDrawer
			? [...openedMenuIdArr]
			: [props.menu.id]
		setAnchorEl(event.currentTarget)

		if (isOpenDrawer) {
			if (openedMenuIdArr.includes(props.menu.id) && isOpenMainMenu) {
				// delete
				const idx = openedMenuIdArr.indexOf(props.menu.id)
				newOpenedMenuIdArr.splice(idx, 1)
			} else if (!openedMenuIdArr.includes(props.menu.id) && !isOpenMainMenu) {
				// add
				newOpenedMenuIdArr.push(props.menu.id)
			}
		}

		reduxDispatch(mainActions.OPENED_MENU_ID_ARR([...newOpenedMenuIdArr]))
		setisOpenMainMenu(!isOpenMainMenu)
	}

	const mainItemIconStyle = {
		color: textColor,
		marginRight: isOpenDrawer ? '0.5rem' : '0rem',
		fontSize: isOpenDrawer ? '20px' : '24px',
	}

	const onCloseSubmenu = () => {
		setAnchorEl(null)
		setisOpenMainMenu(false)
	}

	return (
		<ListItem css={style} disablePadding>
			{isGroup ? (
				<>
					<ListItemButton
						id={`group_${props.menu.id}_button`}
						aria-controls={isOpenMainMenu ? `group_${props.menu.id}_menu` : undefined}
						aria-haspopup="true"
						aria-expanded={isOpenMainMenu ? 'true' : undefined}
						className="menuGroupButton"
						css={[
							{ color: textColor },
							!isOpenDrawer ? { justifyContent: 'center' } : null,
						]}
						onClick={e => onOpenSubmenuClick(e)}
					>
						<Icon name={iconName} style={{ ...mainItemIconStyle }} />
						{isOpenDrawer ? (
							<Text i18n={props.menu.i18n}>{props.menu['name']}</Text>
						) : null}
					</ListItemButton>

					<DrawerMainMenuList
						menu={props.menu}
						anchorEl={anchorEl}
						isOpenMainMenu={isOpenMainMenu}
						onCloseSubmenu={onCloseSubmenu}
					/>
				</>
			) : (
				<DrawerMainMenuItem {...props} isFirstDepth={true} />
			)}
		</ListItem>
	)
}

const style = css({
	display: 'block',

	'.menuGroupButton': {
		height: '2.5rem',
		// background: grey[900],
		// color: '#fff',
	},
})
